<template>
  <div class="showcase-product-picker">
    <div class="form-group">
      <label>Поиск товаров</label>
      <div class="search-field">
        <input
          :value="searchQuery"
          type="text"
          placeholder="Поиск товаров для добавления..."
          :class="{ 'search-field__input--with-clear': searchQuery }"
          @input="onSearchInput($event.target.value)"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="search-field__clear"
          aria-label="Очистить поиск"
          title="Очистить"
          @click="clearSearch"
        >
          ×
        </button>
        <span
          v-if="searchLoading"
          class="spinner"
          aria-label="Поиск..."
        />
      </div>
    </div>

    <div
      v-if="searchResults.length > 0"
      class="showcase-products-search-results"
    >
      <div
        v-for="product in searchResults"
        :key="product.id"
        class="showcase-products-search-item"
      >
        <span>{{ product.name }}</span>
        <button
          type="button"
          class="button button--small"
          :disabled="isProductAdded(product.id)"
          @click="$emit('add-product', product)"
        >
          {{ isProductAdded(product.id) ? "Добавлен" : "Добавить" }}
        </button>
      </div>
    </div>

    <div v-if="rows.length" class="showcase-products-table">
      <div class="showcase-products-table__head">
        <div class="showcase-products-table__cell">Товар</div>
        <div class="showcase-products-table__cell">Цена</div>
        <div class="showcase-products-table__cell showcase-products-table__cell--actions">
          Порядок
        </div>
        <div class="showcase-products-table__cell"></div>
      </div>
      <div class="showcase-products-table__body">
        <div
          v-for="(row, idx) in rows"
          :key="row.product.id"
          class="showcase-products-table__row"
        >
          <div class="showcase-products-table__cell">{{ row.product.name }}</div>
          <div class="showcase-products-table__cell">
            {{ formatPrice(row.product) }} ₽
          </div>
          <div class="showcase-products-table__cell showcase-products-table__cell--actions">
            <button
              type="button"
              class="button button--icon"
              title="Выше"
              :disabled="idx === 0"
              @click="$emit('move-up', idx)"
            >
              ↑
            </button>
            <button
              type="button"
              class="button button--icon"
              title="Ниже"
              :disabled="idx === rows.length - 1"
              @click="$emit('move-down', idx)"
            >
              ↓
            </button>
          </div>
          <div class="showcase-products-table__cell">
            <button
              type="button"
              class="button button--icon button--danger"
              title="Удалить"
              @click="$emit('remove-row', idx)"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-if="rows.length === 0" class="text-muted">
      Добавьте товары для этой вкладки
    </p>
  </div>
</template>

<script>
export default {
  name: "ShowcaseProductPicker",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    searchLoading: {
      type: Boolean,
      default: false,
    },
    searchResults: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    onSearchInput(value) {
      this.$emit("update:search-query", value);
      this.$emit("search-input", value);
    },
    clearSearch() {
      this.$emit("update:search-query", "");
      this.$emit("search-input");
    },
    isProductAdded(productId) {
      return this.rows.some((row) => row.product.id === productId);
    },
    formatPrice(product) {
      const current =
        product?.price?.current ?? product?.price_current ?? product?.price;
      return current != null ? Number(current).toLocaleString("ru-RU") : "—";
    },
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.25rem;
}

.search-field {
  position: relative;
  max-width: 100%;
}

.search-field input[type="text"] {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0.375rem 2.25rem 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.search-field input[type="text"].search-field__input--with-clear {
  padding-right: 4rem;
}

.search-field__clear {
  position: absolute;
  right: 0.375rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #70757a;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
}

.search-field__clear:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #202124;
}

.search-field__clear:focus-visible {
  outline: 2px solid #1a73e8;
  outline-offset: 1px;
}

.search-field .spinner {
  position: absolute;
  right: 2.25rem;
  top: 50%;
  margin-top: -0.5rem;
  width: 1rem;
  height: 1rem;
  border: 2px solid #e0e0e0;
  border-top-color: #666;
  border-radius: 50%;
  animation: showcase-picker-spin 0.7s linear infinite;
}

@keyframes showcase-picker-spin {
  to {
    transform: rotate(360deg);
  }
}

.text-muted {
  color: #666;
  font-size: 0.9rem;
  margin: 0.5rem 0 0;
}

.button {
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 0.875rem;
}

.button:hover:not(:disabled) {
  background: #e9ecef;
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button--small {
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
}

.button--icon {
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
}

.button--danger {
  color: #c62828;
}

.showcase-products-table {
  width: 100%;
  margin-top: 1rem;
}

.showcase-products-table__head,
.showcase-products-table__row {
  display: grid;
  grid-template-columns: 1fr 100px 100px 44px;
  gap: 8px;
  align-items: center;
}

.showcase-products-table__head {
  font-weight: 600;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
}

.showcase-products-table__row {
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 0;
}

.showcase-products-table__cell--actions {
  display: flex;
  gap: 4px;
}

.showcase-products-table__body {
  max-height: 400px;
  overflow-y: auto;
}

.showcase-products-search-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 4px;
  margin-bottom: 0.5rem;
}

.showcase-products-search-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.showcase-products-search-item:last-child {
  border-bottom: none;
}
</style>
